import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save } from 'lucide-react';
import { auth } from '../utils/auth';
import { useLanguage } from '../i18n';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CalculationLogicTab from './settings/CalculationLogicTab';
import EmailTab from './settings/EmailTab';
import AvailabilityTab from './settings/AvailabilityTab';
import GlobalTab from './settings/GlobalTab';
import InfoTextTab from './settings/InfoTextTab';
import AuditLogTab from './settings/AuditLogTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [calculationCode, setCalculationCode] = useState('');
  const [emailTemplates, setEmailTemplates] = useState({});
  const [savingTemplates, setSavingTemplates] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState('');
  const [calculationInfo, setCalculationInfo] = useState('');
  const [savingInfo, setSavingInfo] = useState(false);

  // ─── Data fetching ───
  const fetchConfig = async () => {
    const token = auth.getToken();
    try {
      const response = await axios.get(`${API}/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      toast.error(t('toastConfigLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    const token = auth.getToken();
    try {
      const response = await axios.get(`${API}/config/audit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditLogs(response.data);
    } catch (error) {
      console.error('Audit log fetch error:', error);
    }
  };

  const fetchCalculationCode = async () => {
    const token = auth.getToken();
    try {
      const response = await axios.get(`${API}/calculation-logic`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalculationCode(response.data.code || '');
    } catch (error) {
      console.error('Calculation code fetch error:', error);
    }
  };

  const fetchEmailTemplates = async () => {
    const token = auth.getToken();
    try {
      const response = await axios.get(`${API}/email-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailTemplates(response.data || {});
    } catch (error) {
      console.error('Email templates fetch error:', error);
    }
  };

  const fetchCalculationInfo = async () => {
    const token = auth.getToken();
    try {
      const response = await axios.get(`${API}/calculation-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalculationInfo(response.data.content || '');
    } catch (error) {
      console.error('Calculation info fetch error:', error);
    }
  };

  useEffect(() => {
    const token = auth.getToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchConfig();
    fetchAuditLogs();
    fetchCalculationCode();
    fetchEmailTemplates();
    fetchCalculationInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // ─── Save handlers ───
  const saveConfig = async () => {
    const token = auth.getToken();
    try {
      setSaving(true);
      await axios.patch(`${API}/config`, { data: config }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('toastConfigSaved'));
      fetchAuditLogs();
    } catch (error) {
      toast.error(t('toastSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const saveCalculationCode = async () => {
    const token = auth.getToken();
    try {
      await axios.post(`${API}/calculation-logic`, { code: calculationCode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('toastCalcLogicSaved'));
    } catch (error) {
      toast.error(t('toastCalcLogicError'));
    }
  };

  const saveCalculationInfo = async () => {
    const token = auth.getToken();
    try {
      setSavingInfo(true);
      await axios.post(`${API}/calculation-info`, { content: calculationInfo }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('toastInfoTextSaved'));
    } catch (error) {
      toast.error(t('toastSaveError'));
    } finally {
      setSavingInfo(false);
    }
  };

  const saveEmailTemplates = async () => {
    const token = auth.getToken();
    try {
      setSavingTemplates(true);
      await axios.post(`${API}/email-templates`, emailTemplates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('toastEmailTemplatesSaved'));
    } catch (error) {
      console.error('Template save error:', error);
      toast.error(t('toastSaveErrorDetail', { detail: error.response?.data?.detail || error.message }));
    } finally {
      setSavingTemplates(false);
    }
  };

  const sendTestEmail = async (templateKey) => {
    if (!testEmail) {
      toast.error(t('toastTestEmailRequired'));
      return;
    }
    const token = auth.getToken();
    try {
      setSendingTest(templateKey);
      await axios.post(`${API}/email/test`, {
        template_key: templateKey,
        test_email: testEmail
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('toastTestEmailSent', { key: templateKey, email: testEmail }));
    } catch (error) {
      toast.error(t('toastTestEmailError'));
    } finally {
      setSendingTest('');
    }
  };

  const rollbackConfig = async (version) => {
    if (!window.confirm(`Konfiguration auf Version ${version} zurücksetzen?`)) return;
    const token = auth.getToken();
    try {
      const formData = new FormData();
      formData.append('version', version);
      await axios.post(`${API}/config/rollback`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('toastConfigRestored'));
      fetchConfig();
      fetchAuditLogs();
    } catch (error) {
      toast.error(t('toastResetError'));
    }
  };

  const handleResetToDefault = async (newCode) => {
    const token = auth.getToken();
    try {
      await axios.post(`${API}/calculation-logic`, { code: newCode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('toastDefaultLogicLoaded'));
    } catch (error) {
      toast.error(t('toastCalcLogicError'));
    }
  };

  // ─── Helpers ───
  const updateConfigValue = (path, value) => {
    const keys = path.split('.');
    const newConfig = { ...config };
    let current = newConfig;
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    setConfig(newConfig);
  };

  const updateEmailTemplate = (templateKey, field, value) => {
    setEmailTemplates(prev => ({
      ...prev,
      [templateKey]: { ...prev[templateKey], [field]: value }
    }));
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString('de-DE');

  // ─── Render ───
  if (loading || !config) {
    return (
      <div className="admin-container">
        <div className="admin-content">
          <div style={{ textAlign: 'center', padding: '2rem' }}>Lade Einstellungen...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container" data-testid="admin-settings">
      <div className="admin-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Link to="/admin/dashboard">
            <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} data-testid="back-to-dashboard">
              <ArrowLeft size={18} /> Zurück
            </button>
          </Link>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1B5E20' }}>Einstellungen</h1>
        </div>
        <button onClick={saveConfig} className="btn-primary" disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} data-testid="save-config-btn">
          <Save size={18} /> {saving ? 'Speichere...' : 'Speichern'}
        </button>
      </div>

      <div className="admin-content">
        <Tabs defaultValue="calculation" className="admin-card">
          <TabsList>
            <TabsTrigger value="calculation">Berechnungslogik</TabsTrigger>
            <TabsTrigger value="info">Info-Text</TabsTrigger>
            <TabsTrigger value="availability">Verfügbarkeit</TabsTrigger>
            <TabsTrigger value="email">E-Mail</TabsTrigger>
            <TabsTrigger value="global">Global</TabsTrigger>
            <TabsTrigger value="audit">Verlauf</TabsTrigger>
          </TabsList>

          <TabsContent value="calculation">
            <CalculationLogicTab
              calculationCode={calculationCode}
              setCalculationCode={setCalculationCode}
              saveCalculationCode={saveCalculationCode}
              onResetToDefault={handleResetToDefault}
              t={t}
            />
          </TabsContent>

          <TabsContent value="info">
            <InfoTextTab
              calculationInfo={calculationInfo}
              setCalculationInfo={setCalculationInfo}
              saveCalculationInfo={saveCalculationInfo}
              savingInfo={savingInfo}
            />
          </TabsContent>

          <TabsContent value="availability">
            <AvailabilityTab
              config={config}
              setConfig={setConfig}
              updateConfigValue={updateConfigValue}
              t={t}
            />
          </TabsContent>

          <TabsContent value="email">
            <EmailTab
              config={config}
              updateConfigValue={updateConfigValue}
              emailTemplates={emailTemplates}
              updateEmailTemplate={updateEmailTemplate}
              testEmail={testEmail}
              setTestEmail={setTestEmail}
              saveEmailTemplates={saveEmailTemplates}
              savingTemplates={savingTemplates}
              sendTestEmail={sendTestEmail}
              sendingTest={sendingTest}
            />
          </TabsContent>

          <TabsContent value="global">
            <GlobalTab config={config} updateConfigValue={updateConfigValue} />
          </TabsContent>

          <TabsContent value="audit">
            <AuditLogTab auditLogs={auditLogs} rollbackConfig={rollbackConfig} formatDate={formatDate} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminSettings;
